import { useEffect, useMemo, useState } from "react";
import type { Transaction } from "@money/shared";
import { useReviewQueue, useUpdateTransaction } from "../api/transactions";
import { useCategories } from "../api/categories";
import { useAccounts } from "../api/accounts";
import { Loading, ErrorState } from "../components/States";
import { formatCents, formatDate } from "../lib/format";

export function Review() {
  const queue = useReviewQueue();
  const categories = useCategories();
  const accounts = useAccounts();
  const update = useUpdateTransaction();

  const [focused, setFocused] = useState(0);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const txns = queue.data?.transactions ?? [];
  const cats = categories.data?.categories ?? [];

  const acctName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts.data?.accounts ?? []) m.set(a.id, a.display_name);
    return m;
  }, [accounts.data]);

  const categoryFor = (tx: Transaction) => edits[tx.id] ?? tx.category ?? "";

  const confirm = (tx: Transaction) => {
    const cat = categoryFor(tx).trim();
    update.mutate({
      id: tx.id,
      patch: cat ? { reviewed: 1, category: cat } : { reviewed: 1 },
    });
  };
  const assign = (tx: Transaction, category: string) => {
    setEdits((e) => ({ ...e, [tx.id]: category }));
    update.mutate({ id: tx.id, patch: { reviewed: 1, category } });
  };
  const toggleSkip = (tx: Transaction) =>
    update.mutate({ id: tx.id, patch: { skip_ai: tx.skip_ai === 1 ? 0 : 1 } });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (txns.length === 0) return;
      const idx = Math.min(focused, txns.length - 1);
      const tx = txns[idx];
      if (!tx) return;

      if (e.key === "j" || e.key === "ArrowDown") {
        setFocused((f) => Math.min(txns.length - 1, f + 1));
        e.preventDefault();
      } else if (e.key === "k" || e.key === "ArrowUp") {
        setFocused((f) => Math.max(0, f - 1));
        e.preventDefault();
      } else if (e.key === "Enter") {
        confirm(tx);
        e.preventDefault();
      } else if (e.key === "s") {
        toggleSkip(tx);
      } else if (/^[1-9]$/.test(e.key)) {
        const c = cats[Number(e.key) - 1];
        if (c) assign(tx, c.name);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [txns, focused, cats, edits]);

  if (queue.isPending) {
    return <Loading />;
  }
  if (queue.isError) {
    return <ErrorState onRetry={() => queue.refetch()} />;
  }
  if (txns.length === 0) {
    return (
      <div className="text-slate-400 text-sm py-8 text-center">
        Nothing to review — you're all caught up.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Review queue</h1>
        <span className="text-xs text-slate-500">{txns.length} to review</span>
      </div>
      <p className="text-xs text-slate-500">
        Keys: <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>1</kbd>–<kbd>9</kbd> assign
        category · <kbd>Enter</kbd> confirm · <kbd>s</kbd> toggle skip-AI.
        Number keys map to the categories below in order.
      </p>
      <div className="flex flex-wrap gap-1 text-xs text-slate-500">
        {cats.slice(0, 9).map((c, i) => (
          <span key={c.id} className="rounded bg-slate-800 px-1.5 py-0.5">
            {i + 1}:{c.name}
          </span>
        ))}
      </div>

      <div className="space-y-2">
        {txns.map((tx, i) => (
          <ReviewCard
            key={tx.id}
            tx={tx}
            focused={i === Math.min(focused, txns.length - 1)}
            account={acctName.get(tx.account_id) ?? "—"}
            categories={cats.map((c) => c.name)}
            selected={categoryFor(tx)}
            onFocus={() => setFocused(i)}
            onSelect={(cat) => setEdits((e) => ({ ...e, [tx.id]: cat }))}
            onConfirm={() => confirm(tx)}
            onToggleSkip={() => toggleSkip(tx)}
            busy={update.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function ReviewCard({
  tx,
  focused,
  account,
  categories,
  selected,
  onFocus,
  onSelect,
  onConfirm,
  onToggleSkip,
  busy,
}: {
  tx: Transaction;
  focused: boolean;
  account: string;
  categories: string[];
  selected: string;
  onFocus: () => void;
  onSelect: (cat: string) => void;
  onConfirm: () => void;
  onToggleSkip: () => void;
  busy: boolean;
}) {
  const confidence =
    tx.category_confidence != null ? `${Math.round(tx.category_confidence * 100)}%` : "—";
  // The current/suggested category may not be in the user's category list.
  const options = selected && !categories.includes(selected) ? [selected, ...categories] : categories;

  return (
    <div
      onClick={onFocus}
      className={`rounded-lg border p-4 ${
        focused ? "border-blue-500 bg-slate-900/70" : "border-slate-800 bg-slate-900/40"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{tx.display_name ?? tx.merchant_raw}</div>
          <div className="text-xs text-slate-500">
            {formatDate(tx.posted_at)} · {account}
            {tx.category_source ? ` · ${tx.category_source} ${confidence}` : ""}
            {tx.skip_ai === 1 ? " · skip-AI" : ""}
          </div>
        </div>
        <div className={`tabular-nums font-medium ${tx.amount_cents < 0 ? "text-rose-300" : "text-emerald-300"}`}>
          {formatCents(tx.amount_cents)}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select
          value={selected}
          onChange={(e) => onSelect(e.target.value)}
          className="rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm"
        >
          <option value="">— uncategorized —</option>
          {options.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <button
          onClick={onConfirm}
          disabled={busy}
          className="rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-sm px-3 py-1"
        >
          Confirm
        </button>
        <button
          onClick={onToggleSkip}
          disabled={busy}
          className="rounded border border-slate-700 hover:border-slate-500 text-sm px-3 py-1"
        >
          {tx.skip_ai === 1 ? "Un-skip AI" : "Skip AI"}
        </button>
      </div>
    </div>
  );
}
